type EvaluationMode = 'orderOfOperations' | 'leftToRight';

/**
 * evaluate formula respecting the order of operations
 */
export function evaluateFormula(
  formula: string,
  vars: Record<string, number>,
  decimalPoints = 2,
): number | null {
  return evaluateFormulaInternal(formula, vars, decimalPoints, 'orderOfOperations');
}

/**
 * evaluate formula ignoring the order of operations
 */
export function evaluateFormulaLeftToRight(
  formula: string,
  vars: Record<string, number>,
  decimalPoints = 2,
): number | null {
  return evaluateFormulaInternal(formula, vars, decimalPoints, 'leftToRight');
}

function evaluateFormulaInternal(
  formula: string,
  vars: Record<string, number>,
  decimalPoints: number,
  evaluationMode: EvaluationMode,
): number | null {
  const cleaned = formula.replace(/\s+/g, '');
  // also allow commas for function arguments like MAX(a, b)
  if (!/^[a-zA-Z0-9+\-*/().,]+$/.test(cleaned)) {
    return null;
  }

  let pos = 0;

  function roundToPrecision(value: number): number {
    const precision = Math.pow(10, decimalPoints);

    // workaround for JS floating precision
    return Math.round(value * precision) / precision;
  }

  /**
   * helper to parse MAX and MIN functions.
   * expected format: FUNCTION_NAME(arg1, arg2)
   */
  function parseFunction(name: string): number {
    pos++; // skip '('

    if (name !== 'MAX' && name !== 'MIN') {
      throw new Error(`unsupported function: ${name}`);
    }

    // Parse first argument as a full expression (handles parens, operators inside)
    const val1 = parseCurrentExpression();

    if (pos < cleaned.length && cleaned[pos] === ',') {
      pos++; // skip ','

      // Parse second argument
      const val2 = parseCurrentExpression();

      // skip closing parenthesis for the function call
      if (pos < cleaned.length && cleaned[pos] === ')') {
        pos++;
      }
      else {
        throw new Error(`expected ')' after second argument of ${name}`);
      }

      return name === 'MAX' ? Math.max(val1, val2) : Math.min(val1, val2);
    }

    throw new Error(`${name} requires exactly 2 arguments`);
  }

  function parseExpression(): number {
    let val = parseTerm();
    while (pos < cleaned.length) {
      const op = cleaned[pos];
      if (op !== '+' && op !== '-') {
        break;
      }
      pos++;
      const right = parseTerm();
      if (op === '+') {
        val += right;
      }
      else {
        val -= right;
      }
    }

    return roundToPrecision(val);
  }

  function parseLeftToRightExpression(): number {
    let val = parseFactor();
    while (pos < cleaned.length) {
      const op = cleaned[pos];
      if (op !== '+' && op !== '-' && op !== '*' && op !== '/') {
        break;
      }
      pos++;
      const right = parseFactor();
      if (op === '+') {
        val += right;
      }
      else if (op === '-') {
        val -= right;
      }
      else if (op === '*') {
        val *= right;
      }
      else {
        if (right === 0) {
          throw new Error('division by zero');
        }
        val /= right;
      }
    }

    return roundToPrecision(val);
  }

  function parseTerm(): number {
    let val = parseFactor();
    while (pos < cleaned.length) {
      const op = cleaned[pos];
      if (op !== '*' && op !== '/') {
        break;
      }
      pos++;
      const right = parseFactor();
      if (op === '*') {
        val *= right;
      }
      else {
        if (right === 0) {
          throw new Error('division by zero');
        }
        val /= right;
      }
    }
    return val;
  }

  function parseFactor(): number {
    const ch = cleaned[pos];
    if (ch === '(') {
      pos++;
      const val = parseCurrentExpression();
      if (cleaned[pos] !== ')') {
        throw new Error('mismatched parentheses');
      }
      pos++;
      return val;
    }
    if (ch === '-' || ch === '+') {
      const sign = ch === '-' ? -1 : 1;
      pos++;
      return sign * parseFactor();
    }
    return parseNumberOrVariable();
  }

  function parseNumberOrVariable(): number {
    const start = pos;
    while (pos < cleaned.length && /[a-zA-Z0-9]/.test(cleaned.charAt(pos))) {
      pos++;
    }
    const token = cleaned.slice(start, pos);
    if (!token) {
      throw new Error('expected number or variable');
    }

    // check for function call after identifier (e.g., MAX(...) or MIN(...))
    if (pos < cleaned.length && cleaned[pos] === '(') {
      return parseFunction(token);
    }

    if (/^\d+(\.\d+)?$/.test(token)) {
      return Number(token);
    }

    if (token in vars) {
      const result = vars[token];
      if (result !== undefined) {
        return result;
      }
    }

    throw new Error(`undefined variable: ${token}`);
  }

  function parseCurrentExpression(): number {
    return evaluationMode === 'leftToRight'
      ? parseLeftToRightExpression()
      : parseExpression();
  }

  try {
    const result = parseCurrentExpression();
    if (pos !== cleaned.length) {
      throw new Error('extra characters after expression');
    }
    return result;
  }
  catch {
    return null;
  }
}
